/**
 * Redux slice for user preference queue management.
 * Part of the Atoll shared scaffold — every frontend must include this slice.
 *
 * Preferences are queued locally, debounced, then saved to Atoll's
 * /auth/prefs/{page} endpoint. The processPreferencesQueue function
 * is triggered by the shuteCallbackRegistry middleware on ADD_TO_PREFSQUEUE.
 *
 * Moved from services/redux/prefsSlice.ts.
 * Fixed: useConsole() hooks violation replaced with direct consoleSlice dispatch.
 * Fixed: fnSavePreferences (Sputnik /api/saveprefs) replaced with Atoll /auth/prefs/{page}.
 * Moved: PrefType and UpdateQueueItem to atoll/types/prefTypes.ts.
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AJAXSettings } from "../../types/ajaxSettings";
import { fnDebounceFunc } from "../../services/shared";
import { getAtollServiceURL, getAtollBaseURL } from "../init";
import { fnAJAXFetch } from "../atollFetch";
import { addLog, Severity } from "./consoleSlice";
import { PrefType, UpdateQueueItem } from "../types/prefTypes";

export { PrefType } from "../types/prefTypes";
export type { UpdateQueueItem } from "../types/prefTypes";

function logError(dispatch: any, ...args: any[]) {
  const message = args.map(a =>
    typeof a === 'string' ? a : a instanceof Error ? a.message : JSON.stringify(a)
  ).join(' ');
  if (dispatch) {
    try {
      dispatch(addLog({ severity: Severity.Error, message }));
    } catch {
      console.error(message);
    }
  } else {
    console.error(message);
  }
}

export const processPreferencesQueue = fnDebounceFunc(async (dispatch: any, getState: Function) => {
  try {
    dispatch(setSaving(true));
    const updateQueue = getState().prefs.updateQueue;
    if (!updateQueue || !updateQueue.length) {
      dispatch(setSaving(false));
      return;
    }

    // PrefType.User -> Atoll's own per-user prefs store (/auth/prefs/{page}).
    // Everything else (AdminSetting/AdminStatus/etc) is app-admin config that
    // still lives in Sputnik's own tables -- goes through /api/saveprefs,
    // which branches on item.type to pick the right table.
    const userItems = updateQueue.filter((item: UpdateQueueItem) => item.type === PrefType.User);
    const adminItems = updateQueue.filter((item: UpdateQueueItem) => item.type !== PrefType.User);

    const baseURL = getAtollServiceURL();
    for (const item of userItems) {
      try {
        const page = item.prefname || 'user';
        const url = `${baseURL}auth/prefs/${page}`;
        var tAJAXSettings: AJAXSettings = {
          method: 'PUT',
          url: url,
          data: { [item.keyaddress]: item.keyvalue },
          mode: 'cors',
          credentials: 'include',
          outcome_callback: function (success: boolean, status: number, value: any, headers: any) {
            if (!success) {
              logError(dispatch, `Failed to save pref ${page}.${item.keyaddress}:`, value);
            }
          },
          dispatch: dispatch
        };
        await fnAJAXFetch(tAJAXSettings);
      } catch (itemError) {
        logError(dispatch, 'Failed to save pref:', itemError);
      }
    }

    if (adminItems.length) {
      try {
        const tAdminSettings: AJAXSettings = {
          method: 'PUT',
          url: `${getAtollBaseURL()}api/saveprefs`,
          data: { saveprefs: adminItems },
          mode: 'cors',
          credentials: 'include',
          outcome_callback: function (success: boolean, status: number, value: any, headers: any) {
            if (!success) {
              logError(dispatch, 'Failed to save admin settings:', value);
            }
          },
          dispatch: dispatch
        };
        await fnAJAXFetch(tAdminSettings);
      } catch (itemError) {
        logError(dispatch, 'Failed to save admin settings:', itemError);
      }
    }

    dispatch(clearUpdateQueue());
  } catch (oError) {
    logError(dispatch, oError);
  }
  dispatch(setSaving(false));
}, 2000);

interface PrefsState {
    saving: boolean;
    updateQueue: UpdateQueueItem[];
}

const initialState: PrefsState = {
    saving: false,
    updateQueue: [],
};

const prefsSlice = createSlice({
  name: "prefs",
  initialState,
  reducers: {
    addToQueue: (state, action: PayloadAction<UpdateQueueItem>) => {
      const newItem = action.payload;

      // Find an existing item that matches all properties except keyvalue
      const existingItemIndex = state.updateQueue.findIndex((item) =>
        item.type === newItem.type &&
        item.prefname === newItem.prefname &&
        item.keyaddress === newItem.keyaddress
      );

      if (existingItemIndex !== -1) {
        state.updateQueue[existingItemIndex].keyvalue = newItem.keyvalue;
        state.updateQueue[existingItemIndex].tagid = newItem.tagid;
      } else {
        state.updateQueue.push(newItem);
      }
    },
    clearUpdateQueue: (state) => {
      state.updateQueue = [];
    },
    setSaving: (state, action: PayloadAction<boolean>) => {
      state.saving = action.payload;
    }
  },
});

export const { addToQueue, clearUpdateQueue, setSaving } = prefsSlice.actions;
export const selectUpdateQueue = (state: { prefs: PrefsState }) => state.prefs.updateQueue;
export const itemPendingInSaveQueue = (state: { prefs: PrefsState }, type: PrefType, prefname?: string, keyaddress?: string, keyvalue?: any) => {
  return state.prefs.updateQueue.some(item =>
    item.type === type &&
    (prefname ? item.prefname === prefname : true) &&
    (keyaddress ? item.keyaddress.includes(keyaddress) : true) &&
    (keyvalue ? item.keyvalue === keyvalue : true)
  );
};
export default prefsSlice.reducer;
